/**
 * Session Channel Manager
 *
 * Singleton manager for BroadcastChannel instances used in session management.
 * Ensures only one channel instance per name exists to prevent duplicate
 * message processing and memory leaks.
 *
 * Usage:
 *   import { subscribe, broadcast, closeAllChannels } from './sessionChannelManager';
 *
 *   // Subscribe to messages (returns unsubscribe function)
 *   const unsubscribe = subscribe('bb_auth0_session', (data) => {
 *     console.log('Received:', data);
 *   });
 *
 *   // Broadcast a message
 *   broadcast('bb_auth0_session', { type: 'TOKEN_REFRESHED', expiration: Date.now() });
 *
 *   // Cleanup (usually on app unmount or logout)
 *   closeAllChannels();
 */

// Singleton storage for channel instances
const channels = new Map();

// Message subscribers per channel
const subscribers = new Map();

/**
 * Check if BroadcastChannel is available
 * @returns {boolean}
 */
export const isBroadcastChannelSupported = () => typeof BroadcastChannel !== 'undefined';

/**
 * Get or create a BroadcastChannel instance
 * @param {string} name - Channel name
 * @returns {BroadcastChannel|null} The channel instance, or null if not supported
 */
export const getChannel = (name) => {
  if (!isBroadcastChannelSupported()) {
    return null;
  }

  if (!channels.has(name)) {
    try {
      const channel = new BroadcastChannel(name);

      // Set up unified message handler that routes to subscribers
      channel.onmessage = (event) => {
        const subs = subscribers.get(name);
        if (subs) {
          subs.forEach((callback) => {
            try {
              callback(event.data);
            } catch (error) {
              console.error(`[ChannelManager] Error in subscriber for ${name}:`, error);
            }
          });
        }
      };

      channels.set(name, channel);
    } catch (error) {
      console.error(`[ChannelManager] Failed to create channel ${name}:`, error);
      return null;
    }
  }

  return channels.get(name);
};

/**
 * Subscribe to messages on a channel
 * @param {string} name - Channel name
 * @param {Function} callback - Function to call with message data
 * @returns {Function} Unsubscribe function
 */
export const subscribe = (name, callback) => {
  // Ensure channel exists
  getChannel(name);

  if (!subscribers.has(name)) {
    subscribers.set(name, new Set());
  }

  subscribers.get(name).add(callback);

  // Return unsubscribe function
  return () => {
    const subs = subscribers.get(name);
    if (subs) {
      subs.delete(callback);
    }
  };
};

/**
 * Broadcast a message to all tabs on a channel
 * @param {string} name - Channel name
 * @param {*} data - Data to broadcast
 */
export const broadcast = (name, data) => {
  const channel = getChannel(name);
  if (channel) {
    try {
      channel.postMessage(data);
    } catch (error) {
      console.error(`[ChannelManager] Failed to broadcast on ${name}:`, error);
    }
  }
};

/**
 * Close all channels (call on logout or app unmount)
 */
export const closeAllChannels = () => {
  channels.forEach((channel, name) => {
    try {
      channel.close();
    } catch (error) {
      // Ignore close errors
    }
  });
  channels.clear();
  subscribers.clear();
};
